import {
  BathBathroom,
  Bed,
  Pet,
  UserCouple,
} from '@awazevr/design-system.components.icon'
import { Divider } from '@awazevr/design-system.components.divider'
import { Text } from '@awazevr/design-system.components.text'
import {
  StyledIndividualPropertySpec,
  StyledPropertySpecs,
  StyledSeparator,
} from './Occupancy.styles'

export type OccupancyTranslations = {
  bedrooms: {
    singular: string
    plural: string
  }
  bathrooms: {
    singular: string
    plural: string
  }
  pets: {
    singular: string
    plural: string
  }
  studio: string
  externalBathroom: string
  guests: {
    singular: string
    plural: string
  }
}

export type OccupancyProps = {
  sleeps?: number | null
  bedrooms?: number | null
  bathrooms?: number | null
  pets?: number | null
  translations: OccupancyTranslations
}

const bedroomLabel = (
  bedrooms: number | null | undefined,
  t: OccupancyTranslations,
) => {
  if (bedrooms === 1) return t.bedrooms.singular
  if (bedrooms && bedrooms > 1) return t.bedrooms.plural
  return t.studio
}

const bathroomLabel = (
  bathrooms: number | null | undefined,
  t: OccupancyTranslations,
) => {
  if (bathrooms === 1) return t.bathrooms.singular
  if (bathrooms && bathrooms > 1) return t.bathrooms.plural
  return t.externalBathroom
}

const petsLabel = (
  pets: number | null | undefined,
  t: OccupancyTranslations,
) => {
  if (pets === 1) return t.pets.singular
  return t.pets.plural
}

const guestsLabel = (
  sleeps: number | null | undefined,
  t: OccupancyTranslations,
) => {
  const resolved = sleeps ?? 2
  if (resolved === 1) return t.guests.singular
  return t.guests.plural
}

export const Occupancy = ({
  sleeps,
  bedrooms,
  bathrooms,
  pets,
  translations,
}: OccupancyProps) => {
  return (
    <StyledPropertySpecs>
      <StyledIndividualPropertySpec>
        <UserCouple iconColour="ColorGrey600" size="small" />
        <Text sizes={100} color="ColorGrey600">
          <Text
            as="span"
            type="bold"
            sizes={100}
            color="ColorTextHeadingOnWhite"
          >
            {sleeps || 2}
          </Text>{' '}
          {guestsLabel(sleeps, translations)}
        </Text>
      </StyledIndividualPropertySpec>

      <StyledSeparator aria-hidden="true">
        <Divider isVertical margin={12} />
      </StyledSeparator>

      <StyledIndividualPropertySpec>
        <Bed iconColour="ColorGrey600" size="small" />
        <Text sizes={100} color="ColorGrey600">
          {!!bedrooms && (
            <>
              <Text
                as="span"
                type="bold"
                sizes={100}
                color="ColorTextHeadingOnWhite"
              >
                {bedrooms}
              </Text>{' '}
            </>
          )}
          {bedroomLabel(bedrooms, translations)}
        </Text>
      </StyledIndividualPropertySpec>

      <StyledSeparator aria-hidden="true">
        <Divider isVertical margin={12} />
      </StyledSeparator>

      <StyledIndividualPropertySpec>
        <BathBathroom iconColour="ColorGrey600" size="small" />
        <Text sizes={100} color="ColorGrey600">
          {!!bathrooms && (
            <>
              <Text
                as="span"
                type="bold"
                sizes={100}
                color="ColorTextHeadingOnWhite"
              >
                {bathrooms}
              </Text>{' '}
            </>
          )}
          {bathroomLabel(bathrooms, translations)}
        </Text>
      </StyledIndividualPropertySpec>

      <StyledSeparator aria-hidden="true">
        <Divider isVertical margin={12} />
      </StyledSeparator>

      <StyledIndividualPropertySpec>
        <Pet iconColour="ColorGrey600" size="small" />
        <Text sizes={100} color="ColorGrey600">
          <Text
            as="span"
            type="bold"
            sizes={100}
            color="ColorTextHeadingOnWhite"
          >
            {pets || 0}
          </Text>{' '}
          {petsLabel(pets, translations)}
        </Text>
      </StyledIndividualPropertySpec>
    </StyledPropertySpecs>
  )
}
